import EntityModel from '@/common/models/entity-model';
import SegmentDataModel from '@/common/models/segment-data-model';
import SegmentModel from '@/common/models/segment-model';
import compare from '@/common/helpers/compare';
import isBlank from '@/common/helpers/is-blank';

export default class SegmentCategoryModel extends EntityModel {
    /**
     * @type {String}
     */
    name;

    /**
     * @type {Boolean}
     */
    showHidden;

    /**
     * @type {String}
     */
    searchQuery;

    /**
     * @type {Array.<SegmentModel>}
     */
    $visibleSegments;

    /**
     * @type {SegmentModel[]}
     */
    $hiddenSegments;

    /**
     * @type {SegmentModel[]}
     */
    $activeSegment;

    get label() {
        return SegmentDataModel.CategoriesStructure[this.name].label;
    }

    get types() {
        return SegmentDataModel.CategoriesStructure[this.name].types;
    }

    get containsHiddenSegments() {
        return this.$hiddenSegments.length > 0;
    }

    get containsSegments() {
        return this.$visibleSegments.length > 0 || this.$hiddenSegments.length > 0;
    }

    get segments() {
        return [
            ...this.$visibleSegments,
            ...this.$hiddenSegments,
        ];
    }

    get starredSegment() {
        return this.segments.find(({isVisible, isStarred}) => isVisible && isStarred);
    }

    get hasStarredSegments() {
        return this.starredSegment !== undefined;
    }

    get visibleSegments() {
        return [...this.$visibleSegments];
    }

    get hiddenSegments() {
        return isBlank(this.searchQuery)
            ? [...this.$hiddenSegments]
            : this.$hiddenSegments.filter(({data: {name}}) => new RegExp(this.searchQuery, 'gi').test(name));
    }

    get activeSegment() {
        if (this.$activeSegment) {
            return this.$activeSegment;
        }

        if (this.segments.length > 0) {
            if (this.hasStarredSegments) {
                return this.starredSegment;
            }
            return this.segments[0];
        }

        return null;
    }

    hydrate(
        {
            name = null,
            segments = [],
            activeSegmentId = null,
            showHidden = false,
            searchQuery = '',
        } = {},
    ) {
        if (!SegmentDataModel.CategoriesStructure[name]) {
            throw new Error(`Unknown segment category ${name}`);
        }

        this.name = name;
        this.$visibleSegments = [];
        this.$hiddenSegments = [];
        this.showHidden = showHidden;
        this.searchQuery = searchQuery;

        segments.forEach((segment) => {
            this.addSegment(segment);
        });

        if (activeSegmentId) {
            this.selectSegment(activeSegmentId);
        } else {
            this.deselectSegment();
        }
    }

    /**
     * @param {SegmentModel} segment
     */
    addSegment(segment) {
        if (!(segment instanceof SegmentModel)) {
            segment = new SegmentModel(segment);
        }

        if (!this.types.includes(segment.data.type)) {
            throw new Error('Invalid segment type');
        }
        if (segment.isVisible) {
            this.$visibleSegments.push(segment);
            this.$visibleSegments.sort(SegmentCategoryModel.sortSegments);
        } else {
            this.$hiddenSegments.push(segment);
            this.$hiddenSegments.sort(SegmentCategoryModel.sortSegments);
        }
    }

    /**
     * @param {String} segmentId
     * @param {Boolean} update
     */
    removeSegment(segmentId, update) {
        if (this.$activeSegment && this.$activeSegment.id === segmentId && !update) {
            this.deselectSegment();
        }
        [
            '$visibleSegments',
            '$hiddenSegments',
        ].forEach((name) => {
            this[name] = this[name].filter(({id}) => id !== segmentId);
        });
    }

    /**
     * @param {String} segmentId
     */
    selectSegment(segmentId) {
        const segment = this.segments.find(({id}) => id === segmentId);

        if (!(segment instanceof SegmentModel)) {
            throw new Error(`Segment not found ${segmentId}`);
        }

        this.$activeSegment = segment;
    }

    deselectSegment() {
        this.$activeSegment = null;
    }

    toggleHiddenSegments() {
        this.showHidden = !this.showHidden;
    }

    static sortSegments({data: {name: a}}, {data: {name: b}}) {
        return compare.values(a.toUpperCase(), b.toUpperCase());
    }
}
